import { useState, useContext, useCallback, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import _ from 'lodash'
import {
    PageWrapper,
    Typography,
    Button,
    Tab
} from '../../components/share';
import SnackbarProvider, { snackbarContext } from '../../context/SnackbarProvider';
import { userContext } from '../../context/UserProvider';
import withTabSearchParams from '../../hoc/withTabSearchParams'
import DeliveryTab from './delivery'
import DocTab from './doc'
import TncTab from './tnc'
import PlanTab from './plan'
import { exportCSV, getPermission } from '../../utils'
import { useMutation, useQuery } from '@tanstack/react-query';
import { exportLoginHistory, getConfigByType, updateConfigByType } from '../../apis/globalSettings';
import LoginHistory from './loginHistory';

const Content = styled.div`
    margin: 20px;
    height: 100%;
    overflow: auto;
`

const TABS = [
    {
        name: 'Delivery',
        id: "delivery",
    },
    {
        name: 'T&C',
        id: "tnc",
    },
    {
        name: 'Plan',
        id: "plan",
    },
    {
        name: 'Document Upload',
        id: "docUpload",
    },
    {
        name: "Login History",
        id: "loginHistory",
    }
]

function GlobalSettings({ updateTab, tabIndex = 0 }) {

    const [snackbar, setSnackbar] = useContext(snackbarContext);
    const [user, setUser] = useContext(userContext);

    const globalSettingPermission = getPermission(user?.permissions?.globalSettings)
    const canExportLoginHistoryPermission = {
        canView: getPermission(user?.permissions?.customerLoginExport)?.canView || false,
        canModify: getPermission(user?.permissions?.customerLoginExport)?.canModify || false
    }

    const deliveryRef = useRef({});
    const docRef = useRef({});
    const tncRef = useRef({});
    const planRef = useRef({});
    const loginHistoryRef = useRef({});

    const currentTab = TABS[tabIndex]

    const onApiError = (error) => {
        setSnackbar({
            type: 'error',
            message: `${error?.errorMessage?.en || error?.debugMessage}`
        })
    }

    const { data, error: apiError, isError, isLoading, refetch } = useQuery({
        queryKey: ['globalSettings', { type: currentTab?.id }],
        queryFn: () => getConfigByType(currentTab?.id),
        onError: onApiError,
        enabled: currentTab?.id !== "loginHistory" // login history is not fetched here
    });

    const updateConfigMutation = useMutation({
        mutationFn: (payload) => updateConfigByType(currentTab?.id, payload),
        onSuccess: (data) => {
            setSnackbar({
                type: 'success',
                message: 'Success'
            })
            refetch()
        },
        onError: onApiError
    })

    const exportLoginHistoryMutation = useMutation({
        mutationFn: ({ from, to }) => exportLoginHistory(from, to),
        onSuccess: (data, variables) => {
            // data is already csv attachment; filename=Customer_Login_Status_20250805045521.csv
            // const blob = new Blob([data], { type: 'text/csv' });
            // const url = window.URL.createObjectURL(blob);
            // const a = document.createElement('a');
            // a.href = url;
            // a.download = `Customer_Login_Status_${variables.from.replace(/-/g, '')}_${variables.to.replace(/-/g, '')}.csv`;
            // document.body.appendChild(a);
            // a.click();
            // document.body.removeChild(a);
            // window.URL.revokeObjectURL(url);

            exportCSV(data, `Customer_Login_Status_${variables.from.replace(/-/g, '')}_${variables.to.replace(/-/g, '')}`)

            // Show success snackbar
            setSnackbar({
                type: 'success',
                message: 'Login history exported successfully'
            })

        }
    })

    const onClickTab = (index) => {
        updateTab(TABS[index], index)
    }

    const handleOnSubmit = () => {
        if (TABS[tabIndex].id === "delivery") {
            deliveryRef.current.onSubmit()
        } else if (TABS[tabIndex].id === "docUpload") {
            docRef.current.onSubmit()
        } else if (TABS[tabIndex].id === "tnc") {
            tncRef.current.onSubmit()
        } else if (TABS[tabIndex].id === "plan") {
            planRef.current.onSubmit()
        } else if (TABS[tabIndex].id === "loginHistory") {
            loginHistoryRef.current.onSubmit()
        }
    }

    const handleUpdateConfig = (formValue) => {
        updateConfigMutation.mutate(formValue)
    }

    const handleExportLoginHistory = (formValue) => {
        if (_.isEmpty(formValue)) {
            return setSnackbar({
                type: "error",
                message: "Please select a date range"
            })
        }
        exportLoginHistoryMutation.mutate({
            from: formValue.fromDate,
            to: formValue.toDate
        })
    }

    const renderContent = () => {
        const commonProps = {
            data,
            isLoading,
            setSnackbar,
            permission: globalSettingPermission,
            onSubmit: handleUpdateConfig
        }

        switch (TABS[tabIndex].id) {
            case "delivery": {
                return <DeliveryTab ref={deliveryRef} {...commonProps} />
            }
            case "docUpload": {
                return <DocTab ref={docRef} {...commonProps} />
            }
            case "tnc": {
                return <TncTab ref={tncRef} {...commonProps} />
            }
            case "plan": {
                return <PlanTab ref={planRef} {...commonProps} />
            }
            case "loginHistory": {
                return <LoginHistory ref={loginHistoryRef} {...commonProps} isLoading={false} onSubmit={handleExportLoginHistory} permission={canExportLoginHistoryPermission} />
            }
            default:
                return null;
        }
    }


    const Header = () => {
        return (
            <>
                <Typography bold style={{ flex: 1 }}>{`Global Settings`}</Typography>
            </>
        )
    }

    const CardHeader = () => {
        return (
            <Tab
                tabs={TABS.filter(tab => {
                    if (tab.id === "loginHistory") {
                        return canExportLoginHistoryPermission.canView
                    }
                    return globalSettingPermission.canView
                })}
                selected={tabIndex}
                onChange={onClickTab}
                renderButton={() => {
                    if (currentTab?.id === "loginHistory") {
                        return (
                            <Button
                                style={{
                                    alignSelf: 'flex-end',
                                    margin: `0px 20px 14px 20px`,
                                }}
                                onClick={handleOnSubmit}
                                disabled={!canExportLoginHistoryPermission.canView}
                                loading={exportLoginHistoryMutation?.isLoading}
                            >
                                Export
                            </Button>
                        )
                    }


                    return (
                        <Button
                            style={{
                                alignSelf: 'flex-end',
                                margin: `0px 20px 14px 20px`,
                            }}
                            onClick={handleOnSubmit}
                            disabled={!globalSettingPermission.canModify}
                            loading={updateConfigMutation?.isLoading}
                        >
                            Save
                        </Button>
                    )
                }}
            />
        )
    }

    return (
        <PageWrapper
            renderHeader={Header}
            renderCustomCardHeader={CardHeader}
            loading={currentTab?.id === "loginHistory" ? false : isLoading}
        >
            <Content>
                {renderContent && renderContent()}
            </Content>
        </PageWrapper>
    )
}

export default withTabSearchParams(GlobalSettings, TABS)